import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { SharedChipProps } from '~/eds/src/types/SharedChipProps';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

import { LibraryModelVersion, Tag } from '../types';

export const useGetChip = (
  status: LibraryModelVersion['promptModelPublishStatus'],
): Nullable<SharedChipProps> => {
  const intl = useIntl();
  if (status === null) {
    return null;
  }

  switch (status) {
    case 'draft':
      return {
        text: intl.formatMessage({
          id: 'ModelLibraryHooks.draftStatus',
          defaultMessage: 'Draft',
          description: 'Draft status label for model version',
        }),
        status: 'warning' as const,
      };
    case 'published':
      return {
        text: intl.formatMessage({
          id: 'ModelLibraryHooks.publishedStatus',
          defaultMessage: 'Published',
          description: 'Published status label for model version',
        }),
        status: 'success' as const,
      };
    case 'suspended':
      return {
        text: intl.formatMessage({
          id: 'ModelLibraryHooks.suspendedStatus',
          defaultMessage: 'Suspended',
          description: 'Suspended status label for model version',
        }),
        status: 'danger' as const,
      };
    case 'error':
    case 'in_progress':
    default:
      return {
        text: intl.formatMessage({
          id: 'ModelLibraryHooks.inProgressStatus',
          defaultMessage: 'Publishing',
          description: 'In progress status label for model version',
        }),
        status: 'info' as const,
      };
  }
};

export const useModelLibraryCart = (intl: IntlShape) => {
  const dispatch = useDispatch();
  const trackSegment = useTrackSegment();
  const user = useCurrentUser();

  const addAllModelsToCart = ({
    models,
    category,
    count,
  }: {
    models: LibraryModelVersion[];
    category: Tag;
    count: number;
  }) => {
    dispatch(
      slices.xRayLibrary.actions.addAllModelsToCart({
        models,
        category,
        statusMessage:
          count > 0
            ? {
                autoDismiss: true,
                message: intl.formatMessage(
                  {
                    id: 'ModelLibrary.addedAllToCart',
                    defaultMessage:
                      'Added {count, plural, one {# model} other {# models}} to cart.',
                    description:
                      'Success message when models are added to cart. Example: count = 3',
                  },
                  {
                    count,
                  },
                ),
                status: 'success',
                onDismiss: () =>
                  dispatch(slices.xRayLibrary.actions.clearCartStatusMessage()),
              }
            : undefined,
      }),
    );
    trackSegment('Library model(s) added to cart', {
      models: models.map((model) => ({
        model: model.label,
        modelId: model.libraryModelId,
      })),
      userRole: user.role_uuid?.name ?? user.role,
    });
  };

  const addModelToCart = (model: LibraryModelVersion) => {
    dispatch(
      slices.xRayLibrary.actions.addModelToCart({
        model,
        category:
          model.tags?.[0] ??
          intl.formatMessage({
            id: 'ModelLibrary.unknownCategory',
            defaultMessage: 'Unknown Category',
            description:
              'The category a model is added to when it has no tags.',
          }),
        statusMessage: {
          autoDismiss: true,
          message: intl.formatMessage({
            id: 'ModelLibrary.addedToCart',
            defaultMessage: 'Added to cart.',
            description: 'Success message when model is added to cart',
          }),
          status: 'success',
          onDismiss: () =>
            dispatch(slices.xRayLibrary.actions.clearCartStatusMessage()),
        },
      }),
    );
    trackSegment('Library model(s) added to cart', {
      models: [{ model: model.label, modelId: model.libraryModelId }],
      userRole: user.role_uuid?.name ?? user.role,
    });
  };

  const removeModelFromCart = (model: LibraryModelVersion) => {
    dispatch(
      slices.xRayLibrary.actions.removeModelFromCart({
        model,
        statusMessage: {
          autoDismiss: true,
          message: intl.formatMessage({
            id: 'ModelLibrary.removedFromCart',
            defaultMessage: 'Removed from cart.',
            description: 'Success message when model is removed from cart',
          }),
          status: 'success',
          onDismiss: () =>
            dispatch(slices.xRayLibrary.actions.clearCartStatusMessage()),
        },
      }),
    );
    trackSegment('Library model removed from cart', {
      model: model.label,
      modelId: model.libraryModelId,
      userRole: user.role_uuid?.name ?? user.role,
    });
  };

  return {
    addAllModelsToCart,
    addModelToCart,
    removeModelFromCart,
  };
};
